import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ordersApi, disputesApi } from '../api';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Copy, Package, Loader2, AlertCircle, Clock, AlertTriangle, MessageSquare, Send } from 'lucide-react';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

function formatTimeRemaining(seconds) {
  if (seconds <= 0) return '00:00:00';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [disputeForm, setDisputeForm] = useState({ title: '', description: '' });
  const [submittingDispute, setSubmittingDispute] = useState(false);
  
  // Dispute chat state
  const [dispute, setDispute] = useState(null);
  const [disputeMessage, setDisputeMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchOrder(); }, [id]);
  
  // Scroll to bottom of messages when dispute updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [dispute?.messages]);

  useEffect(() => {
    if (order?.can_dispute && order?.dispute_time_remaining > 0) {
      setTimeRemaining(order.dispute_time_remaining);
      const interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            fetchOrder(); // Refresh to get updated status
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [order?.dispute_time_remaining]);

  const fetchOrder = async () => {
    try {
      const response = await ordersApi.getOne(id);
      setOrder(response.data);
      setTimeRemaining(response.data.dispute_time_remaining || 0);
      
      // If order has an active dispute, fetch dispute details
      if (response.data.dispute_status === 'disputed') {
        fetchDispute();
      }
    } catch (error) {
      toast.error('Order not found');
      navigate('/orders');
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchDispute = async () => {
    try {
      // Get dispute by order ID directly
      const response = await api.get(`/disputes/by-order/${id}`);
      setDispute(response.data);
    } catch (error) {
      console.error('Failed to fetch dispute:', error);
    }
  };
  
  const handleSendDisputeMessage = async (e) => {
    e.preventDefault();
    if (!disputeMessage.trim() || !dispute) return;
    
    setSendingMessage(true);
    try {
      await disputesApi.addMessage(dispute.id, disputeMessage);
      setDisputeMessage('');
      fetchDispute(); // Refresh messages
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleSubmitDispute = async (e) => {
    e.preventDefault();
    if (!disputeForm.title || !disputeForm.description) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmittingDispute(true);
    try {
      await disputesApi.create({
        order_id: order.id,
        title: disputeForm.title,
        description: disputeForm.description
      });
      toast.success('Dispute submitted! Our team will review shortly.');
      setShowDisputeDialog(false);
      navigate('/disputes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setSubmittingDispute(false);
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;
  if (!order) return null;

  const canDispute = order.can_dispute && timeRemaining > 0;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/orders')} className="text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Orders</Button>
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Order #{order.id.slice(0, 8)}</h1>
          <p className="text-zinc-500">{formatDate(order.created_at)}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={order.status === 'completed' ? 'border-green-500/30 text-green-500' : 'border-yellow-500/30 text-yellow-500'}>{order.status}</Badge>
          {order.dispute_status === 'disputed' && <Badge className="bg-red-500 text-white">Disputed</Badge>}
          {order.dispute_status === 'closed' && <Badge variant="outline" className="border-zinc-500/30 text-zinc-500">Dispute Window Closed</Badge>}
        </div>
      </div>

      {/* Dispute Countdown Banner */}
      {canDispute && (
        <Card className="bg-gradient-to-r from-[#FFD700]/20 to-[#FFD700]/5 border-2 border-[#FFD700]/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#FFD700]" />
                </div>
                <div>
                  <p className="font-bold text-white">Dispute Window Open</p>
                  <p className="text-sm text-zinc-400">You can report issues with this order within the time limit</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-3xl font-black text-[#FFD700] font-mono">{formatTimeRemaining(timeRemaining)}</p>
                  <p className="text-xs text-zinc-500">Time Remaining</p>
                </div>
                <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-red-600 hover:bg-red-700 font-bold text-white">
                      <AlertTriangle className="w-4 h-4 mr-2" />Open Dispute
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-2 border-red-500/30 max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-white font-bold text-xl flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500" />Report Issue with Order
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitDispute} className="space-y-4">
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <p className="text-sm text-yellow-500">Please describe your issue clearly. Our team will review and respond as soon as possible.</p>
                      </div>
                      <div>
                        <Label className="text-zinc-300">Issue Title</Label>
                        <Input
                          value={disputeForm.title}
                          onChange={(e) => setDisputeForm({...disputeForm, title: e.target.value})}
                          placeholder="e.g., License key not working"
                          className="bg-[#121212] border-white/10 text-white"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Detailed Description</Label>
                        <Textarea
                          value={disputeForm.description}
                          onChange={(e) => setDisputeForm({...disputeForm, description: e.target.value})}
                          placeholder="Describe the issue in detail. Include any error messages or screenshots if applicable."
                          className="bg-[#121212] border-white/10 text-white h-32"
                          required
                        />
                      </div>
                      <Button type="submit" disabled={submittingDispute} className="w-full bg-red-600 hover:bg-red-700 font-bold">
                        {submittingDispute ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting...</> : 'Submit Dispute'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {order.dispute_status === 'closed' && order.dispute_status !== 'disputed' && (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-zinc-500" />
            <p className="text-zinc-400">The 2-hour dispute window for this order has closed. The order is marked as complete.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader><CardTitle className="text-white">Order Items</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {order.items?.map((item, index) => (
                <div key={index} className="border-b border-white/10 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-[#FFD700]" />
                      <div><p className="font-semibold text-white">{item.title}</p><p className="text-sm text-zinc-500">Qty: {item.quantity} × {formatMoney(item.price)}</p></div>
                    </div>
                    <p className="font-bold text-[#FFD700]">{formatMoney(item.subtotal)}</p>
                  </div>
                  {item.licenses?.length > 0 && (
                    <div className="bg-[#121212] rounded-lg p-3 space-y-2">
                      <p className="text-sm font-semibold text-zinc-400">License Keys:</p>
                      {item.licenses.map((license, i) => (
                        <div key={i} className="flex items-start justify-between gap-2 bg-black/50 rounded p-2">
                          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap flex-1">{license}</pre>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(license)} className="flex-shrink-0 text-zinc-400 hover:text-white"><Copy className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-yellow-500/30">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-400">
                <p className="font-semibold text-yellow-500 mb-1">Important</p>
                <p>Please save your license keys. They will not be shown again after you leave this page. If you have any issues, open a dispute within 2 hours.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader><CardTitle className="text-white">Summary</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-zinc-400">Items</span><span className="text-white">{order.items?.length || 0}</span></div>
              <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-bold"><span className="text-white">Total</span><span className="text-[#FFD700]">{formatMoney(order.total_amount)}</span></div>
              
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dispute Chat Section - Shows when order has active dispute */}
      {order.dispute_status === 'disputed' && dispute && (
        <Card className="bg-[#0A0A0A] border-2 border-red-500/30" data-testid="dispute-chat-section">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-red-500" />
              Dispute Chat - {dispute.title}
              <Badge className={`ml-auto ${dispute.status === 'open' ? 'bg-yellow-500/20 text-yellow-500' : dispute.status === 'resolved' ? 'bg-green-500/20 text-green-500' : 'bg-zinc-500/20 text-zinc-500'}`}>
                {dispute.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          
          {/* Messages */}
          <CardContent className="p-0">
            <div className="h-[300px] overflow-y-auto p-4 space-y-3">
              {dispute.messages?.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">No messages yet</p>
              ) : (
                dispute.messages?.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`flex ${msg.is_admin ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      msg.is_admin 
                        ? 'bg-[#FFD700]/20 border border-[#FFD700]/30' 
                        : 'bg-[#121212] border border-white/10'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold ${
                          msg.is_admin ? 'text-[#FFD700]' : 'text-zinc-400'
                        }`}>
                          {msg.is_admin ? 'Admin' : 'You'}
                        </span>
                        <span className="text-xs text-zinc-500">
                          {formatDate(msg.created_at)}
                        </span>
                      </div>
                      <p className="text-white text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Message Input - Only show if dispute is open */}
            {dispute.status === 'open' && (
              <form onSubmit={handleSendDisputeMessage} className="p-4 border-t border-white/10 flex gap-2">
                <Input
                  value={disputeMessage}
                  onChange={(e) => setDisputeMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="bg-[#121212] border-white/10 text-white"
                  data-testid="dispute-message-input"
                />
                <Button 
                  type="submit" 
                  disabled={sendingMessage || !disputeMessage.trim()} 
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="send-dispute-message-btn"
                >
                  {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            )}
            
            {dispute.status !== 'open' && (
              <div className="p-4 border-t border-white/10 bg-[#121212]">
                <p className="text-center text-zinc-500">
                  This dispute is {dispute.status}. {dispute.status === 'resolved' ? 'Thank you for your patience.' : ''}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
